import React, { useEffect,useState, Suspense } from "react";
import { Button } from "../../../shared/components/ui/button";
import { googleIcon } from "../../../shared/constants/images";
import Image from "next/image";
import { useRouter,useSearchParams } from "next/navigation";
import { verifySession } from "@/features/auth/api/auth.api";
import { getPostLoginRedirectRoute } from "../../../shared/utils/auth-redirect";
import successToast from "@/shared/lib/toast/success.toast";
import { useFormContext } from "react-hook-form";
import { SignupFormType } from "@/features/auth/types/signup.type";

function GoogleAuthButtonContent() {
const [token, setToken] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isBasicInfoValid, setIsBasicInfoValid] = useState(true); // Default to true for login context
  const [isSignupContext, setIsSignupContext] = useState(false);
  
  // Get form context - it might be SignupFormType or LoginFormType
  // Both LoginFormProvider and SignupFormProvider provide a form context
  // We'll check the structure to determine which one
  const formContext = useFormContext<any>();

  // Check if we're in signup context and validate fields accordingly
  useEffect(() => {
    try {
      const values = formContext?.getValues?.();
      // Check if this is a signup context by checking for form1 structure
      if (values && 'form1' in values) {
        setIsSignupContext(true);
        setIsBasicInfoValid(false); // Start as false for signup, will be validated below
        
        // Watch the required fields (fullname, phone) from form1
        // Email is not required for Google sign in button activation
        const checkBasicInfo = () => {
          const currentValues = formContext.getValues();
          const fullname = currentValues?.form1?.fullname?.trim();
          const phone = currentValues?.form1?.phone?.trim();
          
          const isValid = !!(fullname && phone);
          setIsBasicInfoValid(isValid);
        };

        // Initial check
        checkBasicInfo();

        // Watch for changes
        const subscription = formContext.watch((value: any) => {
          checkBasicInfo();
        });
        
        return () => subscription.unsubscribe();
      } else {
        // Login context - button should always be enabled
        setIsSignupContext(false);
        setIsBasicInfoValid(true);
      }
    } catch (error) {
      // Error getting values - assume login context
      setIsSignupContext(false);
      setIsBasicInfoValid(true);
    }
  }, [formContext]);

    const handleGoogleAuth = async () => {
    if (token) {
        // Get user session to determine redirect route based on role
        const session = await verifySession()
        const redirectRoute = session?.role ? getPostLoginRedirectRoute(session.role) : "/home"
        router.push(redirectRoute);
    } else {
      // Only validate and store basic info if we're in signup context
      if (formContext && isSignupContext) {
        const values = formContext.getValues();
        const fullname = values?.form1?.fullname?.trim();
        const email = values?.form1?.email?.trim(); // Email is optional for Google sign in
        const phone = values?.form1?.phone?.trim();
        
        // Email is not required for Google sign in, only fullname and phone are required
        if (!fullname || !phone) {
          return; // Don't proceed if required fields are missing
        }
        
        // Store form data in sessionStorage AND localStorage before redirecting
        // Use both to ensure data persists through OAuth redirects
        // Also store a flag to indicate this is from signup page
        // Email is optional, so only include it if provided
        const signupData: {
          fullname: string;
          phone: string;
          fromSignupPage: boolean;
          email?: string;
        } = {
          fullname,
          phone,
          fromSignupPage: true, // Flag to indicate this is from signup
        };
        
        // Only include email if it was provided
        if (email) {
          signupData.email = email;
        }
        
        console.log('[GoogleAuth] Storing signup info in sessionStorage and localStorage:', signupData);
        sessionStorage.setItem('googleSignupInfo', JSON.stringify(signupData));
        localStorage.setItem('googleSignupInfo', JSON.stringify(signupData)); // Backup in localStorage
        console.log('[GoogleAuth] Signup info stored, redirecting to Google OAuth...');
      } else {
        // Login context - store flag to indicate this is from login page
        // This will be used in the callback to check if email exists
        console.log('[GoogleAuth] Login context - storing fromLoginPage flag');
        sessionStorage.setItem('googleLoginFromLoginPage', 'true');
        localStorage.setItem('googleLoginFromLoginPage', 'true');
        // Clear any existing signup info to prevent false positives
        sessionStorage.removeItem('googleSignupInfo');
        localStorage.removeItem('googleSignupInfo');
      }
      
      const baseUrl = import.meta.env.VITE_BASE_BACKEND_URL || '/api';
      // Pass state parameter to indicate login vs signup
      // Google OAuth will return this state parameter in the callback
      const state = isSignupContext ? 'signup' : 'login';
      
      // Build state params: role only from current URL (for QR flyers), region/team from sessionStorage.
      // Role must come from URL to avoid stale sessionStorage (e.g. after account deletion + recreating).
      const urlRole = searchParams.get('role');
      const unifiedAuthRegion = sessionStorage.getItem('unifiedAuthRegion');
      const unifiedAuthRole = urlRole || null; // Only trust role from current page URL
      const unifiedAuthTeam = sessionStorage.getItem('unifiedAuthTeam');
      
      console.log('[GoogleAuth] Building state - role from URL only:', {
        urlRole,
        unifiedAuthRegion,
        unifiedAuthRole,
        unifiedAuthTeam,
      });
      
      let stateWithParams = state;
      if (unifiedAuthRegion || unifiedAuthRole || unifiedAuthTeam) {
        const params: string[] = [];
        if (unifiedAuthRegion) params.push(`region=${unifiedAuthRegion}`);
        if (unifiedAuthRole && (unifiedAuthRole === 'volunteer' || unifiedAuthRole === 'homeowner')) {
          params.push(`role=${unifiedAuthRole}`);
        }
        if (unifiedAuthTeam) params.push(`team=${encodeURIComponent(unifiedAuthTeam)}`);
        if (params.length > 0) {
          stateWithParams = `${state}|${params.join('&')}`;
        }
        console.log('[GoogleAuth] Built state with params:', stateWithParams);
      }
      
      // Check if we have a previously selected Google account email (from a failed login attempt)
      // This allows us to use login_hint to pre-select the account and skip the selection page
      let selectedEmail = sessionStorage.getItem('selectedGoogleAccountEmail');
      if (!selectedEmail) {
        selectedEmail = localStorage.getItem('selectedGoogleAccountEmail');
      }
      
      // Build the OAuth URL with state and optional login_hint
      let oauthUrl = `${baseUrl}/auth/google?state=${encodeURIComponent(stateWithParams)}`;
      if (selectedEmail) {
        oauthUrl += `&login_hint=${encodeURIComponent(selectedEmail)}`;
        console.log('[GoogleAuth] Using login_hint to pre-select Google account:', selectedEmail);
      }
      
      window.location.href = oauthUrl;
        } 
    }

    
useEffect(() => {
  const token = searchParams.get("token");
  if (token) {
    document.cookie = `aim-auth=${token}; path=/; max-age=${7 * 24 * 60 * 60}`;

    const reloaded = localStorage.getItem("reloadedAfterLogin");

    // Helper function to update profile with Google signup info
    const updateProfileWithSignupInfo = async (session: any, token: string): Promise<boolean> => {
      // Check both sessionStorage and localStorage (backup)
      let storedInfo = sessionStorage.getItem('googleSignupInfo');
      if (!storedInfo) {
        storedInfo = localStorage.getItem('googleSignupInfo');
        console.log('[GoogleAuth] Found signup info in localStorage (backup)');
      }
      
      if (!storedInfo || !session?.sub) {
        console.warn('No signup info or session ID available for profile update', {
          hasStoredInfo: !!storedInfo,
          hasSessionId: !!session?.sub,
        });
        return false; // No signup info to update
      }

      try {
        const signupInfo = JSON.parse(storedInfo);
        const baseUrl = import.meta.env.VITE_BASE_BACKEND_URL || '/api';
        
        // Validate and transform phone to E.164 format before sending
        const { transformToE164, isValidPhoneNumber } = await import('@/shared/lib/utils/phone.utils');
        let phoneE164: string | undefined = undefined;
        
        if (signupInfo.phone && signupInfo.phone.trim()) {
          // Only transform if phone is valid
          if (isValidPhoneNumber(signupInfo.phone)) {
            phoneE164 = transformToE164(signupInfo.phone);
          } else {
            console.warn('Invalid phone number format, skipping phone update:', signupInfo.phone);
          }
        }
        
        // Build update data - only include fields that have values
        const updateData: { fullname?: string; phone?: string } = {};
        
        if (signupInfo.fullname && signupInfo.fullname.trim()) {
          updateData.fullname = signupInfo.fullname.trim();
        }
        
        if (phoneE164) {
          updateData.phone = phoneE164;
        }
        
        // Don't proceed if we have nothing to update
        if (!updateData.fullname && !updateData.phone) {
          console.warn('No valid data to update in profile');
          sessionStorage.removeItem('googleSignupInfo');
          return false;
        }
        
        console.log('Updating profile with Google signup info:', {
          userId: session.sub,
          updateData,
        });
        
        // Update user with the stored information - wait for it to complete
        const response = await fetch(`${baseUrl}/my-profile/${session.sub}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        });

        if (response.ok) {
          const responseData = await response.json().catch(() => ({}));
          console.log('Profile updated successfully:', responseData);
          // Clear the stored info after successful update (from both storage locations)
          sessionStorage.removeItem('googleSignupInfo');
          localStorage.removeItem('googleSignupInfo');
          return true;
        } else {
          // Log error for debugging
          const errorText = await response.text().catch(() => '');
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { message: errorText || 'Unknown error' };
          }
          console.error('Failed to update user profile:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData,
            requestData: updateData,
          });
          return false;
        }
      } catch (error) {
        console.error('Failed to update user info:', error);
        return false;
      }
    };

    if (!reloaded) {
      localStorage.setItem("reloadedAfterLogin", "true");

      verifySession()
        .then(async (session: any) => {
          console.log('[GoogleAuth] OAuth callback - checking for signup info', {
            sessionId: session?.sub,
            sessionRole: session?.role,
          });
          
          // Check if we have stored Google signup info (new registration)
          // Check both sessionStorage and localStorage (backup)
          // Only treat as new registration if it has the fromSignupPage flag
          let storedInfo = sessionStorage.getItem('googleSignupInfo');
          if (!storedInfo) {
            storedInfo = localStorage.getItem('googleSignupInfo');
            console.log('[GoogleAuth] Found signup info in localStorage (backup)');
          }
          console.log('[GoogleAuth] Stored signup info:', storedInfo);
          
          let isNewRegistration = false;
          if (storedInfo) {
            try {
              const signupInfo = JSON.parse(storedInfo);
              console.log('[GoogleAuth] Parsed signup info:', signupInfo);
              isNewRegistration = signupInfo.fromSignupPage === true;
              console.log('[GoogleAuth] Is new registration?', isNewRegistration);
            } catch (e) {
              console.error('[GoogleAuth] Failed to parse stored info:', e);
              // If parsing fails, it's not a new registration
              isNewRegistration = false;
            }
          }
          
          // Update profile with signup info and wait for it to complete
          if (isNewRegistration && session?.sub) {
            console.log('[GoogleAuth] Starting profile update for new registration');
            const updateSuccess = await updateProfileWithSignupInfo(session, token);
            console.log('[GoogleAuth] Profile update result:', updateSuccess);
          } else {
            console.log('[GoogleAuth] Skipping profile update - not a new registration or missing session ID', {
              isNewRegistration,
              hasSessionId: !!session?.sub,
            });
          }

          // Redirect to Profile for new registrations, otherwise use normal login redirect
          const redirectRoute = isNewRegistration 
            ? "/profile"
            : (session?.role ? getPostLoginRedirectRoute(session.role) : "/home");

          window.location.href = redirectRoute;
          successToast("Google login successful");
        })
        .catch(() => {
          window.location.href = "/home";
        });
    } else {
      // 🔑 Always clear so next login works
      localStorage.removeItem("reloadedAfterLogin");

      verifySession()
        .then(async (session: any) => {
            console.log('[GoogleAuth] OAuth callback (reload) - checking for signup info', {
              sessionId: session?.sub,
              sessionRole: session?.role,
            });
          
          // Check if we have stored Google signup info (new registration)
          // Check both sessionStorage and localStorage (backup)
          // Only treat as new registration if it has the fromSignupPage flag
          let storedInfo = sessionStorage.getItem('googleSignupInfo');
          if (!storedInfo) {
            storedInfo = localStorage.getItem('googleSignupInfo');
            console.log('[GoogleAuth] Found signup info in localStorage (backup reload)');
          }
          console.log('[GoogleAuth] Stored signup info (reload):', storedInfo);
          
          let isNewRegistration = false;
          if (storedInfo) {
            try {
              const signupInfo = JSON.parse(storedInfo);
              console.log('[GoogleAuth] Parsed signup info (reload):', signupInfo);
              isNewRegistration = signupInfo.fromSignupPage === true;
              console.log('[GoogleAuth] Is new registration? (reload)', isNewRegistration);
            } catch (e) {
              console.error('[GoogleAuth] Failed to parse stored info (reload):', e);
              // If parsing fails, it's not a new registration
              isNewRegistration = false;
            }
          }
          
              // Update profile with signup info and wait for it to complete
              if (isNewRegistration && session?.sub) {
                console.log('[GoogleAuth] Starting profile update for new registration (reload)');
                const updateSuccess = await updateProfileWithSignupInfo(session, token);
                console.log('[GoogleAuth] Profile update result (reload):', updateSuccess);
              } else {
                console.log('[GoogleAuth] Skipping profile update (reload) - not a new registration or missing session ID', {
                  isNewRegistration,
                  hasSessionId: !!session?.sub,
                });
              }
          
          // Redirect to Profile for new registrations, otherwise use normal login redirect
          const redirectRoute = isNewRegistration 
            ? "/profile"
            : (session?.role ? getPostLoginRedirectRoute(session.role) : "/home");

          window.location.href = redirectRoute;
          successToast("Google login successful");
        })
        .catch(() => {
          window.location.href = "/home";
        });
    }
  }
}, [searchParams]);



  useEffect(() => {
    const cookieToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("aim-auth="))
      ?.split("=")[1];
    setToken(cookieToken || null);
  }, []);

    return (
        <Button 
            type='button'
            variant={"outline"} 
            className='rounded-full w-full text-foreground border-input'
            onClick={handleGoogleAuth}
            disabled={isSignupContext && !isBasicInfoValid}
            showLoader={false}
        >
            <Image
                src={googleIcon}
                alt='Google svg icon'
                className='w-5 h-5'
            />
            Continue with Google
        </Button>
    )
}

function GoogleAuthButton() {
  return (
    <Suspense fallback={
      <Button 
        type='button'
        variant={"outline"} 
        className='rounded-full w-full text-foreground border-input'
        disabled
      >
        <Image
          src={googleIcon}
          alt='Google svg icon'
          className='w-5 h-5'
        />
        Loading...
      </Button>
    }>
      <GoogleAuthButtonContent />
    </Suspense>
  )
}

export default GoogleAuthButton;
